import { EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  DatePicker,
  Form,
  Image,
  Input,
  Modal,
  Select,
  Switch,
  Table,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AppContext } from '../../AppContext';
import { BACKEND_DATE_FORMAT, USER } from '../../common/constants';
import { fileUpload, getBase64, validateImage } from '../../common/utils';
import CommonSelect from '../../components/CommonSelect';
import { INCIDENT_MASTERS } from '../incidentMaster/graphql/Queries';
import CommonPreviewTable from './components/CommonPreviewTable';
import { CREATE_INCIDENT_REPORT_ADMIN } from './graphql/Mutations';
import {
  GET_SIGNED_URLS_FOR_INCIDENT_IMAGES,
  SEARCH_USERS,
} from './graphql/Queries';

const { Option } = Select;
const { TextArea } = Input;

const ImageUploadCell = ({ record, form }) => {
  const [showModal, setShowModal] = useState(null);
  const [selectedImage, setSelectedImage] = useState([]);

  const fieldKey = `image-${record.key}`;
  const selectedImagesFile = form.getFieldValue(fieldKey) ?? [];

  const [thumbUrl, setThumbUrl] = useState('');

  useEffect(() => {
    if (selectedImagesFile?.[0]?.originFileObj) {
      getBase64(selectedImagesFile?.[0].originFileObj, (imageUrl) =>
        setThumbUrl(imageUrl),
      );
    }
  }, [selectedImagesFile]);

  const handleChange = (info) => {
    validateImage(info, form, setSelectedImage, fieldKey, false, {
      width: 40,
      height: 40,
    });
  };

  if (!record?.isSaved) {
    return (
      <>
        <Form.Item
          name={fieldKey}
          rules={[{ required: true, message: 'Please Upload Images!' }]}
        >
          {!selectedImagesFile?.length && (
            <Button
              icon={<UploadOutlined />}
              className="upload-img-btn"
              onClick={() => setShowModal(record?.key)}
            >
              Click To Upload
            </Button>
          )}

          <span className="image-preview-wrapper">
            {selectedImagesFile?.length > 0 && (
              <div className="d-flex gap-8">
                <Image
                  width={30}
                  height={30}
                  src={thumbUrl}
                  className="pointer image-preview-icon"
                  preview={{ visible: false }}
                />
                <span>
                  {selectedImagesFile?.length > 1
                    ? ` + ${selectedImagesFile?.length - 1}`
                    : ''}
                </span>
                <div
                  onClick={() => setShowModal(record?.key)}
                  className="pointer"
                >
                  <EditOutlined />
                </div>
              </div>
            )}
          </span>
        </Form.Item>
        <Modal
          open={showModal === record?.key}
          closable={false}
          title="Upload Images"
          centered
          footer={[
            <Button
              key="submit"
              type="primary"
              onClick={() => setShowModal(null)}
            >
              Save
            </Button>,
          ]}
        >
          <Upload
            name="image"
            listType="picture-card"
            maxCount={5}
            accept=".png,.jpg,.jpeg"
            fileList={selectedImage}
            beforeUpload={() => false}
            onChange={handleChange}
            className="avatar-uploader"
            multiple
            showUploadList={{
              showDownloadIcon: false,
              showPreviewIcon: false,
              showRemoveIcon: true,
            }}
          >
            {selectedImage?.length !== 5 && <PlusOutlined />}
          </Upload>
        </Modal>
      </>
    );
  }

  // For saved rows
  return (
    <span className="image-preview-wrapper">
      {record?.images?.length > 0 ? (
        <>
          <Image
            width={40}
            height={40}
            src={record?.images?.[0]?.thumbUrl}
            className="pointer image-preview-icon"
            preview={{ visible: false }}
          />
        </>
      ) : (
        <span>No Images</span>
      )}
      {record?.images?.length > 1 ? ` + ${record?.images?.length - 1}` : ''}
    </span>
  );
};

const CreateIncident = () => {
  const initialData = Array.from({ length: 10 }, (_, index) => ({
    key: index.toString(),
    reportingForUserId: '',
    date: '',
    projectName: '',
    severity: '',
    description: '',
    isSaved: false,
    isDiscussionRequired: false,
  }));
  // eslint-disable-next-line no-undef
  const user = JSON.parse(localStorage.getItem(USER));

  const [dataSource, setDataSource] = useState(initialData);
  const [form] = Form.useForm();
  const [userSearch, setUserSearch] = useState();
  const [visible, setVisible] = useState(false);
  const [toyPreviewImage, setToyPreviewImage] = useState();
  const [isSubmitLoading, setIsSubmitLoading] = useState({});

  const { dispatch = () => {}, state: { showPrompt = false } = {} } =
    useContext(AppContext) || {};

  const [getSignedUrls] = useLazyQuery(GET_SIGNED_URLS_FOR_INCIDENT_IMAGES, {
    fetchPolicy: 'network-only',
    onError() {
      setIsSubmitLoading({});
    },
  });

  const [createIncidentReportAdmin] = useMutation(
    CREATE_INCIDENT_REPORT_ADMIN,

    {
      onCompleted: () => {
        setIsSubmitLoading({});
        dispatch({
          type: 'SET_SHOW_PROMPT',
          data: false,
        });
      },
      onError() {
        setIsSubmitLoading({});
      },
    },
  );

  const saveRow = async (key) => {
    setIsSubmitLoading({
      [key]: true,
    });
    try {
      await form.validateFields([
        `reportingForUserId-${key}`,
        `date-${key}`,
        `projectName-${key}`,
        `severity-${key}`,
        `description-${key}`,
        `isDiscussionRequired-${key}`,
      ]);
      const row = dataSource?.find((item) => item?.key === key);
      const selectedImage =
        form.getFieldValue(`image-${key}`)?.length > 0
          ? form.getFieldValue(`image-${key}`)
          : [];
      const updatedRow = {
        ...row,
        reportingForUserId: form.getFieldValue(`reportingForUserId-${key}`),
        date: form.getFieldValue(`date-${key}`),
        projectName: form.getFieldValue(`projectName-${key}`),
        severity: form.getFieldValue(`severity-${key}`),
        description: form.getFieldValue(`description-${key}`),
        isSaved: true,
        isDiscussionRequired: form.getFieldValue(`isDiscussionRequired-${key}`),
        incidentMasterId: form.getFieldValue(`incidentMasterId-${key}`),
        images:
          form.getFieldValue(`image-${key}`)?.length > 0
            ? form.getFieldValue(`image-${key}`)
            : [],
      };

      let images = [];
      if (selectedImage?.length > 0) {
        const uuid = uuidv4();

        const fileKeyArray = selectedImage?.map((item) => {
          const { name } = item;
          const ext = name?.substring(name?.lastIndexOf('.') + 1);
          const timestamp = Date?.now();
          const filename = name?.split('.')?.slice(0, -1)?.join('.');
          const newFilename = `${timestamp}_${filename}.${ext}`;
          const fileKey = `incident/${uuid}/${newFilename}`;
          return fileKey;
        });
        const res = await getSignedUrls({
          variables: {
            data: {
              fileNames: fileKeyArray,
            },
          },
        });
        if (res?.data) {
          try {
            const response = await Promise.all(
              res?.data?.getSignedUrlsForIncidentImages?.map(
                async (signedData, index) => {
                  try {
                    await fileUpload(
                      signedData?.signedRequest,
                      selectedImage?.[index]?.originFileObj,
                    );
                    return signedData?.key;
                  } catch (error) {
                    throw new Error(
                      `${selectedImage?.[index]?.name} upload failed. Please try again.`,
                    );
                  }
                },
              ),
            );
            images = response;
          } catch (error) {
            //
          }
        }
      }

      const res = await createIncidentReportAdmin({
        variables: {
          data: {
            date: dayjs(updatedRow?.date).format(BACKEND_DATE_FORMAT),
            description: updatedRow?.description,
            projectName: updatedRow?.projectName,
            reportingForUserId: [...updatedRow?.reportingForUserId],
            severity: updatedRow?.severity,
            isDiscussionRequired: updatedRow?.isDiscussionRequired,
            incidentMasterId: updatedRow?.incidentMasterId,
            ...(images?.length > 0 && { imageUrls: images }),
          },
        },
      });

      if (res?.data) {
        setDataSource((prevData) =>
          prevData?.map((item) =>
            item?.key === updatedRow?.key ? updatedRow : item,
          ),
        );
      }
    } catch (error) {
      setIsSubmitLoading((prev) => ({ ...prev, [key]: false }));
      return error;
    }
  };

  const handleUserSearch = debounce((value) => {
    setUserSearch(value);
  }, 500);

  const handleAddRow = () => {
    const newRow = {
      key: dataSource?.length?.toString(),
      reportingForUserId: '',
      date: '',
      projectName: '',
      severity: '',
      description: '',
      isSaved: false,
      isDiscussionRequired: false,
    };
    setDataSource([...dataSource, newRow]);
  };

  const disableFutureDates = (current) =>
    current && current > dayjs().endOf('day');

  const columns = [
    {
      title: 'Raised For',
      dataIndex: 'reportingForUserId',
      width: '15%',
      render: (_, record) => (
        <Form.Item
          name={`reportingForUserId-${record.key}`}
          rules={[{ required: true, message: 'Please Enter Raised For!' }]}
        >
          <CommonSelect
            placeholder="Select User"
            showSearch
            query={SEARCH_USERS}
            onSearch={handleUserSearch}
            mode="multiple"
            responsePath="searchUsers"
            valuePath="id"
            labelPath="displayName"
            fetchPolicy="network-only"
            variables={{
              searchStrings: userSearch,
              idsToExclude: user?.id ? [user?.id] : [],
            }}
            useEffectDeps={[userSearch]}
            virtual={false}
            disabled={record?.isSaved}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Incident Date',
      dataIndex: 'date',
      width: '10%',
      render: (_, record) => (
        <Form.Item
          name={`date-${record.key}`}
          rules={[{ required: true, message: 'Please Enter Incident Date!' }]}
        >
          <DatePicker
            disabledDate={disableFutureDates}
            className="incident-form"
            disabled={record?.isSaved}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'incidentMasterId',
      width: '15%',
      render: (_, record) => (
        <Form.Item
          name={`incidentMasterId-${record.key}`}
          rules={[
            { required: true, message: 'Please Select Incident Master!' },
          ]}
        >
          <CommonSelect
            placeholder="Select Category"
            showSearch
            query={INCIDENT_MASTERS}
            onSearch={handleUserSearch}
            responsePath="incidentMasters.data"
            valuePath="id"
            labelPath="name"
            fetchPolicy="network-only"
            variables={{
              filters: { search: userSearch, statusFilter: 'ACTIVE' },
            }}
            useEffectDeps={[userSearch]}
            virtual={false}
            disabled={record?.isSaved}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Project/Task Name',
      dataIndex: 'projectName',
      render: (_, record) => (
        <Form.Item
          name={`projectName-${record.key}`}
          rules={[
            { required: true, message: 'Please Enter Project/Task Name!' },
          ]}
        >
          <Input placeholder="Enter Text Here" disabled={record?.isSaved} />
        </Form.Item>
      ),
    },
    {
      title: 'Image',
      dataIndex: 'image',
      render: (_, record) => <ImageUploadCell record={record} form={form} />,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      render: (_, record) => (
        <Form.Item
          name={`severity-${record.key}`}
          rules={[{ required: true, message: 'Please Select Severity!' }]}
        >
          <Select placeholder="Select Severity" disabled={record?.isSaved}>
            <Option value="LOW">Low</Option>
            <Option value="MEDIUM">Medium</Option>
            <Option value="HIGH">High</Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: 'Describe The Incident',
      dataIndex: 'description',
      render: (_, record) => (
        <Form.Item
          name={`description-${record.key}`}
          className="tbl-description-input"
          rules={[
            { required: true, message: 'Please Enter Description!' },
            { min: 100, message: 'Minimum 100 Characters Are Required!' },
            { max: 5000, message: 'Maximum 5000 Characters Are Allowed!' },
          ]}
        >
          <TextArea
            showCount
            maxLength={5000}
            placeholder="Enter Text Here"
            disabled={record?.isSaved}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Is Discussion Required?',
      dataIndex: 'isDiscussionRequired',
      width: '5%',
      render: (_, record) => (
        <Form.Item name={`isDiscussionRequired-${record?.key}`}>
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled={record?.isSaved}
          />
        </Form.Item>
      ),
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Form.Item>
          <Button
            type="primary"
            size="small"
            disabled={
              record?.isSaved || Object.values(isSubmitLoading)?.some(Boolean)
            }
            onClick={() => saveRow(record?.key)}
            loading={isSubmitLoading?.[record?.key]}
          >
            Save
          </Button>
        </Form.Item>
      ),
    },
  ];

  return (
    <div className="create-incident-container">
      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />
      <Form
        form={form}
        component={false}
        onFieldsChange={() => {
          if (!showPrompt) {
            dispatch({
              type: 'SET_SHOW_PROMPT',
              data: true,
            });
          }
        }}
      >
        <Table
          className="incident-table"
          dataSource={dataSource}
          columns={columns}
          rowClassName="editable-row"
          pagination={false}
        />
      </Form>
      <div className="d-flex justify-center">
        <Button
          onClick={handleAddRow}
          type="primary"
          className="d-flex justify-center mt-16"
        >
          Add Row
        </Button>
      </div>
    </div>
  );
};
export default CreateIncident;
